import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import CourseCard from '../components/CourseCard';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';

const WishlistPage = () => {
  const { user, token } = useAuthStore();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    wishlist,
    getWishlist,
    isLoading,
    error
  } = useCourseStore();

  const enrolledCourses = useCourseStore(state => state.enrolledCourses);
  const fetchMyCourses = useCourseStore(state => state.fetchMyCourses);

  useEffect(() => {
    getWishlist();
  }, [getWishlist]);

  const handleEnroll = async (courseSlug, courseId) => {
    if (!token) {
      localStorage.setItem('redirectAfterLogin', `/courses/${courseSlug}/${courseId}`);
      toast.info('Please login or create a student account to continue');
      navigate('/login');
      return;
    }
    try {
      const result = await useCourseStore.getState().enrollInCourse(courseId);
      if (result?.data?.authorizationUrl) {
        window.location.href = result.data.authorizationUrl;
      } else {
        toast.success('Enrolled successfully!');
      }
    } catch (err) {
      toast.error('Failed to enroll in course');
    }
  };

  useEffect(() => {
    if (user?.role === "student") {
      const loadCourseData = async () => { await fetchMyCourses() };
      loadCourseData();
    }
  }, [user]);

  if (isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">My Wishlist</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Courses you've saved for later
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
              {error}
            </div>
          )}

          {wishlist?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  gap-6">
              {wishlist.map((course) => (
                <CourseCard
                  key={course._id}
                  course={course}
                  onEnroll={() => handleEnroll(course?.slug, course?._id)}
                  isEnrolled={enrolledCourses.some(c => c._id === course._id) ? true : false}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">❤️</div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Your wishlist is empty</h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                Browse courses and add them to your wishlist for later
              </p>
              <button
                onClick={() => navigate('/courses')}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Browse Courses
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WishlistPage;